import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { Booking, BookingPlayer, BookingStatus } from '../model/booking.model';
import { environment } from '../../environments/environment';

export type WaitlistScope = 'active' | 'past' | 'all';

interface WaitlistCriteriaDto {
  isoDate?: string;
  dayOfWeek?: string;
  dayOfMonth?: number;
  monthShort?: string;
  year?: number;
  timeOfDay?: string;
  players?: number;
  holes?: number;
  maxPricePerPatron?: number;
}

interface WaitlistCourseDto {
  name?: string;
  location?: string;
  locationDisplay?: string;
  countryCode?: string;
  country?: string;
  locationId?: string;
}

interface WaitlistCustomerDto {
  name?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string | null;
}

interface WaitlistDto {
  id?: string;
  externalId?: string;
  status?: string;
  statusLabel?: string;
  statusColor?: string;
  course?: WaitlistCourseDto;
  criteria?: WaitlistCriteriaDto;
  customer?: WaitlistCustomerDto;
  paymentType?: string;
  expiresAt?: string;
  expiresInDays?: number;
  confirmedBookingId?: string | null;
  chatId?: string;
  place?: string;
  coordinates?: { lat?: number; lng?: number } | null;
  createdAt?: string;
  updatedAt?: string;
}

interface WaitlistsResponse {
  results?: WaitlistDto[];
  data?: WaitlistDto[];
  scope?: WaitlistScope;
  page?: number;
  limit?: number;
  totalPages?: number;
  totalResults?: number;
  activeCount?: number;
  pastCount?: number;
}

export interface WaitlistsPageOptions {
  page?: number;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class WaitlistService {
  private readonly _http = inject(HttpClient);

  private readonly _activeWaitlists = signal<Booking[]>([]);
  private readonly _pastWaitlists = signal<Booking[]>([]);
  private readonly _loading = signal(false);
  private readonly _activeCount = signal(0);
  private readonly _pastCount = signal(0);

  readonly activeWaitlists = this._activeWaitlists.asReadonly();
  readonly pastWaitlists = this._pastWaitlists.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly activeCount = this._activeCount.asReadonly();
  readonly pastCount = this._pastCount.asReadonly();

  getById(id: string): Booking | undefined {
    return (
      this._activeWaitlists().find((w) => w.waitlistId === id || w.id === id) ??
      this._pastWaitlists().find((w) => w.waitlistId === id || w.id === id)
    );
  }

  loadWaitlists(scope: WaitlistScope = 'active', opts: WaitlistsPageOptions = {}): Observable<Booking[]> {
    this._loading.set(true);
    const { page = 1, limit = 20 } = opts;
    const params = new HttpParams()
      .set('scope', scope)
      .set('page', page)
      .set('limit', limit);
    return this._http
      .get<WaitlistsResponse | WaitlistDto[]>(`${environment.apiUrl}waitlists`, { params })
      .pipe(
        tap((res) => this.captureCounts(res)),
        map((res) =>
          Array.isArray(res) ? res : res?.results ?? res?.data ?? [],
        ),
        map((dtos: WaitlistDto[]) => dtos.map((dto) => this.mapDtoToBooking(dto))),
        tap({
          next: (rows) => {
            if (scope === 'past') {
              this._pastWaitlists.set(rows);
            } else {
              this._activeWaitlists.set(rows);
            }
            this._loading.set(false);
          },
          error: () => this._loading.set(false),
        }),
      );
  }

  loadWaitlistById(id: string): Observable<Booking> {
    return this._http
      .get<WaitlistDto | { waitlist?: WaitlistDto; data?: WaitlistDto }>(`${environment.apiUrl}waitlists/${id}`)
      .pipe(
        map((res) => {
          if (res && typeof res === 'object' && 'waitlist' in res && res.waitlist) return res.waitlist;
          if (res && typeof res === 'object' && 'data' in res && res.data) return res.data;
          return res as WaitlistDto;
        }),
        map((dto) => this.mapDtoToBooking(dto)),
        tap((row) => {
          // Upsert so a direct/refreshed detail load (where the list is empty) still
          // caches the criteria-based detail for getById() to return.
          const list = this._activeWaitlists();
          const idx = list.findIndex((w) => w.waitlistId === row.waitlistId || w.id === row.id);
          if (idx >= 0) {
            const next = [...list];
            next[idx] = row;
            this._activeWaitlists.set(next);
          } else {
            this._activeWaitlists.set([...list, row]);
          }
        }),
      );
  }

  private captureCounts(res: WaitlistsResponse | WaitlistDto[]): void {
    if (Array.isArray(res)) return;
    if (typeof res?.activeCount === 'number') this._activeCount.set(res.activeCount);
    if (typeof res?.pastCount === 'number') this._pastCount.set(res.pastCount);
  }

  private mapDtoToBooking(dto: WaitlistDto): Booking {
    const isoDate = dto.criteria?.isoDate;
    const parsed = isoDate ? new Date(isoDate) : null;
    const date = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();

    const playerCount = dto.criteria?.players ?? 0;
    const players: BookingPlayer[] = Array.from({ length: playerCount }, () => ({} as BookingPlayer));

    const status = this.mapWaitlistStatus(dto.status);
    const statusLabel = dto.statusLabel ?? this.defaultLabel(dto.status);
    const statusColor = dto.statusColor ?? this.defaultColor(dto.status);

    const rawCoords = dto.coordinates;
    const coordinates =
      rawCoords && typeof rawCoords.lat === 'number' && typeof rawCoords.lng === 'number'
        ? { lat: rawCoords.lat, lng: rawCoords.lng }
        : undefined;

    const waitlistId = dto.id ?? '';

    return {
      id: waitlistId || dto.externalId || '',
      date,
      time: this.timeOfDayLabel(dto.criteria?.timeOfDay),
      status,
      statusLabel,
      statusColor,
      courseName: dto.course?.name ?? '',
      location: dto.course?.locationDisplay || dto.course?.location || '',
      locationId: dto.course?.locationId,
      place: dto.place,
      coordinates,
      players,
      holes: dto.criteria?.holes,
      contact: dto.customer
        ? {
            name: dto.customer.name,
            email: dto.customer.email,
            phone: dto.customer.phone,
            avatarUrl: dto.customer.avatarUrl ?? undefined,
          }
        : undefined,
      isWaitlist: true,
      waitlistId,
      waitlistReferenceId: dto.externalId,
      expiresAt: dto.expiresAt,
      expiresInDays: dto.expiresInDays,
      confirmedBookingId: dto.confirmedBookingId ?? null,
      paymentType: dto.paymentType,
      requestedDate: isoDate,
      timeOfDay: dto.criteria?.timeOfDay,
    };
  }

  private mapWaitlistStatus(raw: string | undefined): BookingStatus {
    switch ((raw ?? '').toLowerCase()) {
      case 'confirmed':
        return 'booked';
      case 'expired':
      case 'cancelled':
      case 'canceled_by_user':
      case 'auto_confirm_failed':
      case 'failed':
        return 'cancelled';
      case 'processing':
        return 'processing';
      case 'waitlisted':
      default:
        return 'waitlist';
    }
  }

  private defaultLabel(raw: string | undefined): string {
    switch ((raw ?? '').toLowerCase()) {
      case 'waitlisted': return 'Waitlisted';
      case 'confirmed': return 'Confirmed';
      case 'expired': return 'Expired';
      case 'cancelled':
      case 'canceled_by_user': return 'Cancelled';
      case 'auto_confirm_failed': return 'Expired';
      case 'failed': return 'Failed';
      case 'processing': return 'Processing';
      default: return 'Waitlisted';
    }
  }

  private defaultColor(raw: string | undefined): string {
    switch ((raw ?? '').toLowerCase()) {
      case 'confirmed': return 'green';
      case 'expired':
      case 'cancelled':
      case 'canceled_by_user': return 'grey';
      case 'auto_confirm_failed':
      case 'failed': return 'red';
      case 'processing':
      case 'waitlisted':
      default: return 'yellow';
    }
  }

  private timeOfDayLabel(t: string | undefined): string {
    if (!t || t === 'any') return 'Any time';
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
}
